import React, { useMemo, useRef, useState } from "react";

import { Box, Button, Image } from "@chakra-ui/react";
import { ReactComponent as Tier1Icon } from "assets/icons/tier-1.svg";
import { ReactComponent as Tier3Icon } from "assets/icons/tier-3.svg";
import { COLORS_MAX_LENGTH_INDEX } from "features/business-compliance/consts";
import { getNextColor } from "features/business-compliance/tab-items";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "routes/constants";
import { getRandomNumber } from "utils";
import { EP_POPUP_CONTAINER, START_INDEX, ZERO } from "utils/constants";

import { Heading3, Heading4, Paragraph3, Paragraph4 } from "ui/typography";

import { IEmployeeInfoPopUpPosition, IEmployeeInfoPopUpProps } from "./types";

const POPUP_Y_OFFSET = 280;
const POPUP_X_OFFSET = 100;
const TIMER = 100;
const TIER_ONE = 1;
const TIER_THREE = 3;

const tierLevelIcon: Record<number, React.ReactNode> = {
  [ZERO]: "",
  [TIER_ONE]: (
    <Tier1Icon className="absolute bottom-[-10px] right-0 w-[40px] h-[40px]" />
  ),
  [TIER_THREE]: (
    <Tier3Icon className="absolute bottom-[-10px] right-0 w-[40px] h-[40px]" />
  ),
};

export const EmployeeInfoPopUp = (props: IEmployeeInfoPopUpProps) => {
  const {
    employeeInfo: {
      fullName,
      level,
      emailAddress,
      primaryAcctNumber,
      phoneNumber,
      imageUrl,
      id,
      tierNumber,
    },
    lastColor,
  } = props;
  const navigate = useNavigate();
  const [showPopup, setShowPopup] = useState(false);
  const [position, setPosition] = useState<IEmployeeInfoPopUpPosition>({
    x: 0,
    y: 0,
  });

  const hoverTimeout = useRef<NodeJS.Timeout | null>(null);
  const handleMouseEnter = (event: React.MouseEvent) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setPosition({
      x: rect.left + rect.width + POPUP_X_OFFSET,
      y: rect.top + window.scrollY - POPUP_Y_OFFSET,
    });

    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
      hoverTimeout.current = null;
    }
    setShowPopup(true);
  };

  const handleMouseLeave = () => {
    hoverTimeout.current = setTimeout(() => {
      setShowPopup(false);
    }, TIMER);
  };

  const handleViewIndividualCustomer = () => {
    navigate(`${ROUTES.INDIVIDUALS_DETAILS_PAGE.replace(":individualId", id)}`);
  };

  const bgColor = useMemo(() => {
    return getNextColor(
      getRandomNumber(START_INDEX, COLORS_MAX_LENGTH_INDEX),
      lastColor
    );
  }, [lastColor]);

  return (
    <Box className="relative inline-block">
      <Box
        className="flex items-center gap-3 cursor-pointer"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Box
          className="h-8 w-8 rounded-[50%] flex justify-center items-center flex-shrink-0"
          css={{
            backgroundColor: bgColor,
            color: "#ffffff",
          }}
        >
          <span className="capitalize text-[12.58px] leading-[19.2px] font-medium text-center">
            {fullName.charAt(START_INDEX)}
          </span>
        </Box>
        <Box>
          <Heading4
            fontSize="14px"
            fontWeight="600"
            lineHeight="20px"
            textColor="#09090B"
            className="capitalize"
          >
            {fullName}
          </Heading4>
          <Paragraph4
            fontSize="12px"
            lineHeight="16px"
            textColor="#718096"
          >
            {emailAddress}
          </Paragraph4>
        </Box>
      </Box>
      {showPopup &&
        ReactDOM.createPortal(
          <Box
            css={{
              position: "absolute",
              top: `${position.y}px`,
              left: `${position.x}px`,
              width: "350px",
              height: "306px",
              borderRadius: "12px",
              padding: "24px",
              transform: "translateX(-50%)",
              backgroundColor: "#ffffff",
              display: showPopup ? "flex" : "none",
              flexDirection: "column",
              gap: "24px",
              boxShadow: "0px 0px 24px 4px #10182814",
              zIndex: 20,
            }}
            onMouseEnter={() => {
              if (hoverTimeout.current) {
                clearTimeout(hoverTimeout.current);
                hoverTimeout.current = null;
              }
              setShowPopup(true);
            }}
            onMouseLeave={handleMouseLeave}
          >
            <Box className="flex flex-col gap-5 h-[190px]">
              <Box className="flex gap-3 items-center">
                <Box className="relative w-[81px]">
                  {imageUrl ? (
                    <Image
                      src={imageUrl}
                      alt=""
                      className="w-[64px] h-[64px] rounded-[50%]"
                    />
                  ) : (
                    <Box
                      className="w-[64px] h-[64px] rounded-[50%] flex justify-center items-center"
                      css={{
                        backgroundColor: bgColor,
                        color: "#ffffff",
                        flexShrink: 0,
                      }}
                    >
                      <span className="capitalize text-[20px] leading-[19.2px] font-medium text-center">
                        {fullName.charAt(START_INDEX)}
                      </span>
                    </Box>
                  )}
                  {level !== "N/A" && <>{tierLevelIcon?.[tierNumber]}</>}
                </Box>
                <Box className="flex flex-col gap-2">
                  <Heading3
                    lineHeight="27.32px"
                    fontWeight="700"
                    className="capitalize"
                  >
                    {fullName}
                  </Heading3>
                  <Paragraph3
                    lineHeight="18.2px"
                    fontWeight="600"
                    textColor="#111827"
                  >
                    {level}
                  </Paragraph3>
                </Box>
              </Box>
              <Box className="h-fit flex-wrap min-h-[19px] max-h-fit flex items-center justify-between">
                <Paragraph3
                  lineHeight="19.12px"
                  textColor="#718096"
                  className="text-left"
                >
                  Primary Account Number
                </Paragraph3>
                <Paragraph3
                  lineHeight="19.12px"
                  textColor="#1A202C"
                  fontWeight="600"
                  className="text-right"
                >
                  {primaryAcctNumber}
                </Paragraph3>
              </Box>
              <Box className="h-fit flex-wrap min-h-[19px] max-h-fit flex items-center justify-between">
                <Paragraph3
                  lineHeight="19.12px"
                  textColor="#718096"
                  className="text-left"
                >
                  Email Address
                </Paragraph3>
                <Paragraph3
                  lineHeight="19.12px"
                  textColor="#1A202C"
                  fontWeight="600"
                  className="text-right"
                >
                  {emailAddress}
                </Paragraph3>
              </Box>
              <Box className="h-fit flex-wrap min-h-[19px] max-h-fit flex items-center justify-between">
                <Paragraph3
                  lineHeight="19.12px"
                  textColor="#718096"
                  className="text-left"
                >
                  Phone Number
                </Paragraph3>
                <Paragraph3
                  lineHeight="19.12px"
                  textColor="#1A202C"
                  fontWeight="600"
                  className="text-right"
                >
                  {phoneNumber}
                </Paragraph3>
              </Box>
            </Box>
            <Box className="h-[44px] w-full">
              <Button
                className={`h-[44px] rounded-lg border border-[#D0D5DD] py-[10px] px-[18px] font-semibold text-[16px] leading-6 text-[#344054] w-full ${!id ? "cursor-not-allowed opacity-40" : "cursor-pointer"}`}
                css={{
                  boxShadow: "0px 1px 2px 0px #1018280D",
                }}
                onClick={id ? handleViewIndividualCustomer : undefined}
              >
                View Individual Customer
              </Button>
            </Box>
          </Box>,
          document.getElementById(EP_POPUP_CONTAINER)!
        )}
    </Box>
  );
};
